import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  ShieldCheck, 
  CheckCircle2, 
  FileCheck, 
  BarChart3, 
  Search, 
  AlertTriangle,
  Target,
  Lock,
  Cpu,
  Users,
  TrendingUp,
  Award,
  ArrowRight,
  Database,
  Eye,
  Fingerprint,
  Scale
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const technicalWorkstreams = [
  {
    icon: FileCheck,
    title: 'Performance-Based Financing (PBF) Validation',
    description: 'End-to-end verification of PBF schemes through rigorous onsite and remote audits.',
    features: [
      {
        name: 'Quantity Verification',
        detail: 'Verifying that reported service volumes match facility records and community reality.'
      },
      {
        name: 'Quality of Care (QoC) Scoring',
        detail: 'Using standardized clinical checklists to ensure volume does not compromise clinical excellence.'
      }
    ]
  },
  {
    icon: Database,
    title: 'Advanced Data Integrity Analysis',
    description: 'Rigorous set-theoretic approach to the verification process.',
    features: [
      {
        name: 'Causal Recipe Analysis',
        detail: 'Identifying whether performance spikes represent genuine improvement or data manipulation.'
      },
      {
        name: 'Contextual Factor Mapping',
        detail: 'Multi-dimensional assessment combining facility, regional, and systemic variables.'
      }
    ]
  },
  {
    icon: Shield,
    title: 'Counter-Fraud & Risk Mitigation',
    description: 'Safeguarding against systemic leakage with our "Frontier Defense" approach.',
    features: [
      {
        name: 'Beneficiary Tracing',
        detail: 'Direct community engagement to verify services were received by reported populations.'
      },
      {
        name: 'Predictive Anomaly Detection',
        detail: 'AI-driven models identifying reporting patterns deviating from clinical norms.'
      }
    ]
  }
];

const valuePropositions = [
  {
    icon: Scale,
    title: 'Institutional Neutrality',
    description: 'Absolute independence from implementing partners, ensuring reports meeting the most stringent international standards of transparency.'
  },
  {
    icon: TrendingUp,
    title: 'Marginal Utility Auditing',
    description: 'Insights into where verification costs can be optimized without compromising the integrity of results.'
  },
  {
    icon: Cpu,
    title: 'Systemic Leapfrogging',
    description: 'Transitioning verification from slow, paper-based manual audits to real-time, digital-first verification ecosystems.'
  }
];

const coreServices = [
  {
    icon: Eye,
    title: 'External Verification Agency (EVA) Roles',
    description: 'Serving as the third-party validator for national health programs.'
  },
  {
    icon: BarChart3,
    title: 'Data Quality Assessments (DQA)',
    description: 'Strengthening the reliability of National Health Management Information Systems (NHMIS).'
  },
  {
    icon: FileCheck,
    title: 'Compliance & Covenant Auditing',
    description: 'Verifying adherence to technical and financial triggers of international grants and loans.'
  }
];

export const IVAPage = () => {
  return (
    <div data-testid="iva-page" className="bg-slate-50">
      {/* Hero Section - Institutional Excellence */}
      <section className="relative min-h-[80vh] flex items-center bg-slate-900 overflow-hidden" data-testid="iva-hero">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        
        <div className="container-main relative z-10 pt-32 pb-20">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            {/* Badge */}
            <motion.div 
              variants={itemVariants}
              className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/30 px-4 py-2 mb-8"
            >
              <ShieldCheck size={18} className="text-teal-400" />
              <span className="font-mono text-xs uppercase tracking-widest text-teal-400">
                Independent Verification Agency
              </span>
            </motion.div>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6"
            >
              The Gold Standard in{' '}
              <span className="text-teal-400">Results-Based Accountability</span>
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-xl text-slate-300 leading-relaxed mb-8 max-w-3xl"
            >
              In the complex landscape of global health financing, the transition from 
              "activity-based" to "results-based" models requires an unshakeable foundation 
              of trust. HSL serves as a premier Independent Verification Agency (IVA), 
              providing the rigorous, third-party validation necessary to bridge the gap 
              between financial investment and verified health impact.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="bg-slate-800/50 border-l-4 border-teal-500 p-6 max-w-2xl"
            >
              <p className="text-white font-heading text-lg italic">
                "We do not just audit data; we certify the integrity of the health system's outcomes."
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 font-bold tracking-wide uppercase text-sm transition-colors inline-flex items-center gap-2 group"
                data-testid="iva-cta-contact"
              >
                Request IVA Services
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/methodology"
                className="bg-transparent border-2 border-slate-600 hover:border-slate-400 text-white px-8 py-4 font-bold tracking-wide uppercase text-sm transition-colors"
                data-testid="iva-cta-methodology"
              >
                Our Methodology
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Capability Indicators */}
        <div className="absolute bottom-0 left-0 right-0 bg-slate-800/80 border-t border-slate-700">
          <div className="container-main py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: 'Rigorous', label: 'Methodology' },
                { value: 'Global', label: 'Standards Compliant' },
                { value: 'Real-Time', label: 'Digital Verification' },
                { value: 'End-to-End', label: 'Audit Capability' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <span className="font-heading text-2xl md:text-3xl text-teal-400 block">{stat.value}</span>
                  <span className="text-slate-400 text-xs uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IVA Mandate Section */}
      <section className="section-padding bg-white" data-testid="iva-mandate">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
                Our Mandate
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Trust Through Rigor
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                As an Independent Verification Agency, HSL provides the objective lens 
                required by multilateral donors and national governments to ensure that 
                every dollar disbursed is backed by a verified unit of health progress.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our approach is defined by the <span className="font-semibold text-slate-800">Ambidextrous Strategy</span>: 
                maintaining forensic accuracy in current data verification (Exploitation) while 
                deploying digital innovations to prevent future data fragility (Exploration).
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-50 border border-slate-200 p-5">
                  <div className="w-10 h-10 bg-slate-900 flex items-center justify-center mb-3">
                    <Target size={20} className="text-teal-400" />
                  </div>
                  <h4 className="font-heading text-slate-900 font-semibold mb-1">Exploitation</h4>
                  <p className="text-slate-500 text-sm">Forensic accuracy in current data verification</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-5">
                  <div className="w-10 h-10 bg-teal-600 flex items-center justify-center mb-3">
                    <Cpu size={20} className="text-white" />
                  </div>
                  <h4 className="font-heading text-slate-900 font-semibold mb-1">Exploration</h4>
                  <p className="text-slate-500 text-sm">Digital innovations to prevent data fragility</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {/* Verification Seal Visual */}
              <div className="bg-slate-900 p-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-teal-500/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                
                <div className="relative text-center">
                  <div className="w-32 h-32 mx-auto mb-6 border-4 border-teal-500 rounded-full flex items-center justify-center">
                    <ShieldCheck size={56} className="text-teal-400" />
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-2">IVA Certified</h3>
                  <p className="text-slate-400 text-sm mb-6">Independent Verification Agency</p>
                  
                  <div className="space-y-3">
                    {['Data Integrity Verified', 'Results Authenticated', 'Compliance Confirmed'].map((item, i) => (
                      <div key={i} className="flex items-center justify-center gap-2 text-slate-300">
                        <CheckCircle2 size={16} className="text-teal-400" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Workstreams */}
      <section className="section-padding bg-slate-100" data-testid="iva-workstreams">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Our Technical Expertise
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Technical Workstreams
            </h2>
            <p className="text-slate-600 text-lg">
              Our verification methodology combines rigorous field auditing with advanced 
              analytical techniques to deliver unparalleled accuracy.
            </p>
          </motion.div>

          <div className="space-y-8">
            {technicalWorkstreams.map((workstream, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 overflow-hidden"
                data-testid={`workstream-${index}`}
              >
                <div className="grid lg:grid-cols-3">
                  {/* Header */}
                  <div className="bg-slate-900 p-8 flex flex-col justify-center">
                    <div className="w-14 h-14 bg-teal-500/20 border border-teal-500/50 flex items-center justify-center mb-4">
                      <workstream.icon size={28} className="text-teal-400" />
                    </div>
                    <h3 className="font-heading text-xl text-white mb-2">{workstream.title}</h3>
                    <p className="text-slate-400 text-sm">{workstream.description}</p>
                  </div>
                  
                  {/* Features */}
                  <div className="lg:col-span-2 p-8">
                    <div className="grid md:grid-cols-2 gap-6">
                      {workstream.features.map((feature, fi) => (
                        <div key={fi} className="flex gap-4">
                          <div className="w-8 h-8 bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle2 size={16} className="text-teal-600" />
                          </div>
                          <div>
                            <h4 className="font-heading text-slate-900 font-semibold mb-1">{feature.name}</h4>
                            <p className="text-slate-500 text-sm">{feature.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="section-padding bg-slate-900" data-testid="iva-value">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-teal-400 mb-4 block">
              Why HSL
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              The IVA Value Proposition
            </h2>
            <p className="text-slate-400 text-lg">
              What sets HSL apart as your Independent Verification Agency partner.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {valuePropositions.map((prop, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-800 border border-slate-700 p-8 hover:border-teal-500/50 transition-colors group"
                data-testid={`value-prop-${index}`}
              >
                <div className="w-14 h-14 bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mb-6 group-hover:bg-teal-500/20 transition-colors">
                  <prop.icon size={28} className="text-teal-400" />
                </div>
                <h3 className="font-heading text-xl text-white mb-3">{prop.title}</h3>
                <p className="text-slate-400 leading-relaxed">{prop.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Service Offerings */}
      <section className="section-padding bg-white" data-testid="iva-services">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mb-16"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Service Offerings
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Core IVA Service Offerings
            </h2>
            <p className="text-slate-600 text-lg">
              Comprehensive verification services tailored for national health programs 
              and international development initiatives.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {coreServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="border-2 border-slate-200 p-8 hover:border-slate-900 transition-colors group"
                data-testid={`service-${index}`}
              >
                <div className="w-14 h-14 bg-slate-100 flex items-center justify-center mb-6 group-hover:bg-slate-900 transition-colors">
                  <service.icon size={28} className="text-slate-700 group-hover:text-teal-400 transition-colors" />
                </div>
                <h3 className="font-heading text-xl text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-500 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-100" data-testid="iva-cta">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 p-12 md:p-16 relative overflow-hidden"
          >
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-500/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Shield size={32} className="text-teal-400" />
                  <span className="font-mono text-xs uppercase tracking-widest text-teal-400">
                    Partner With HSL
                  </span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                  Ready to Establish Results-Based Accountability?
                </h2>
                <p className="text-slate-400 text-lg">
                  Contact our IVA team to discuss how HSL can serve as your trusted 
                  Independent Verification Agency.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 font-bold tracking-wide uppercase text-sm transition-colors inline-flex items-center gap-2 group whitespace-nowrap"
                  data-testid="iva-final-cta"
                >
                  Request IVA Proposal
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/technical-pillars"
                  className="bg-transparent border-2 border-slate-600 hover:border-slate-400 text-white px-8 py-4 font-bold tracking-wide uppercase text-sm transition-colors whitespace-nowrap"
                >
                  Learn More
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
